"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";

import { formatTime } from "@/features/flight-estimator/_logic/utils";

import { useEstimator } from "../../_context/EstimatorContext";
import type { DepartureFlights } from "../../_logic/city-flights";
import { ENTER, FADE } from "../../_logic/motion";
import { CellSkeleton } from "../workspace/CellSkeleton";
import { GroupFlightRow } from "./GroupFlightRow";

interface DepartureRowProps {
  departure: DepartureFlights;
  destination: string;
  expanded: boolean;
  searching: boolean;
  onToggle: () => void;
}

/**
 * One departure airport: average cost per traveler, average flight time, travelers, layovers
 * and total, expanding into its traveler groups.
 * @param props - The departure, destination, open state, search state and toggle.
 * @returns The row.
 * @throws Never.
 */
export const DepartureRow = ({ departure, destination, expanded, searching, onToggle }: DepartureRowProps) => {
  // Hooks
  const { money } = useEstimator();
  const reduceMotion = useReducedMotion();

  // Derived
  const pending = searching && departure.avgCost === null;
  const stats: Array<[string, React.ReactNode]> = [
    [
      "Avg per traveler",
      pending ? <CellSkeleton /> : departure.avgCost === null ? "No fare" : money.format(departure.avgCost),
    ],
    ["Avg flight time", departure.avgMinutes === null ? "–" : formatTime(departure.avgMinutes)],
    ["Travelers", departure.travelers],
    ["Layovers", departure.layovers],
  ];

  // Render
  return (
    <li>
      {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3.5 text-left transition-colors hover:bg-gray-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 sm:px-5"
      >
        <span className="flex min-w-[120px] flex-1 flex-col">
          <span className="text-sm font-semibold text-gray-900">{departure.code}</span>
          <span className="text-xs font-medium text-gray-600">{departure.name}</span>
        </span>
        {stats.map(([label, value]) => (
          <span key={label} className="hidden flex-col items-end md:flex">
            <span className="text-xs font-medium text-gray-600">{label}</span>
            <span className="text-sm font-medium tabular-nums text-gray-900">{value}</span>
          </span>
        ))}
        <span className="flex min-w-[96px] flex-col items-end">
          <span className="text-xs font-medium text-gray-600">Total</span>
          <span className="text-base font-bold tabular-nums text-gray-900">
            {pending ? <CellSkeleton width="w-20" /> : departure.total ? money.format(departure.total) : "–"}
          </span>
        </span>
        <ChevronDown
          className={`size-4 shrink-0 text-gray-500 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div key="groups" {...(reduceMotion ? FADE : ENTER)}>
            <div className="flex flex-col divide-y divide-gray-100 border-t border-gray-100 pl-4 sm:pl-8">
              {departure.groups.map((group) => (
                <GroupFlightRow key={group.key} group={group} destination={destination} searching={searching} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};
